import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  MIN_SEGMENT_SIZE,
  type ReportDto,
  type ReportHighlightDto,
} from '@genoma/shared';

import { Centered } from '../../components/Centered.tsx';
import { useI18n } from '../../i18n/index.tsx';
import { fetchMyReport, fetchReport } from '../../services/report.service.ts';
import { useAuthStore } from '../../stores/auth.store.ts';
import { ActionPlan } from './ActionPlan.tsx';
import { BehaviorDotPlot } from './BehaviorDotPlot.tsx';
import { CommentSummary } from './CommentSummary.tsx';
import { ReviewBar } from './ReviewBar.tsx';

/**
 * The leadership report.
 *
 * Follows the approved layout: what this is, the numbers, what they say, and
 * what to do about them. The last of those is the coach's to write, and until
 * a coach has, it says so plainly rather than showing an empty frame or
 * invented text.
 */

const SectionHeading = ({
  number,
  kicker,
  title,
}: {
  number: string;
  kicker: string;
  title: string;
}) => (
  <div className="mt-12 flex break-inside-avoid items-baseline gap-3 border-t border-line pt-5">
    <span className="font-mono text-[26px] leading-none font-bold text-line">{number}</span>
    <span>
      <span className="block font-mono text-[10px] tracking-[0.14em] text-teal uppercase">
        {kicker}
      </span>
      <h2 className="text-[22px] leading-tight font-extrabold tracking-[-0.02em]">{title}</h2>
    </span>
  </div>
);

const HighlightCard = ({
  highlight,
  showGap,
}: {
  highlight: ReportHighlightDto;
  showGap?: boolean;
}) => {
  const { t } = useI18n();
  return (
    <li className="border-t border-line-soft py-3 first:border-t-0">
      <p className="text-[14.5px]">
        <span className="mr-1.5 font-mono text-[12px] font-bold text-teal">
          {String(highlight.number).padStart(2, '0')}
        </span>
        <b className="font-bold">{highlight.name}</b>
        <span className="text-soft">
          {' — '}
          {t('report.globalIs', { value: highlight.global.toFixed(1) })}
          {showGap && highlight.self !== null && highlight.gap !== null && (
            <>
              {' '}
              {t('report.youSeeYourself', {
                self: highlight.self.toFixed(1),
                gap: `${highlight.gap > 0 ? '+' : ''}${highlight.gap.toFixed(1)}`,
              })}
            </>
          )}
        </span>
      </p>
      {highlight.anchor && highlight.currentStep && !showGap && (
        <p className="mt-1 text-[13px] text-soft">
          <span className="font-semibold text-ink">
            {t('report.currentStep', { step: t(`scale.step${highlight.currentStep}`) })}
          </span>{' '}
          {highlight.anchor}
        </p>
      )}
      {/* Inside the top band the rung ahead is the rung they are on, so the
          anchor would print twice, word for word. Say it once. */}
      {highlight.nextStep && highlight.nextStep.step !== highlight.currentStep && (
        <p className="mt-1 text-[13px] text-soft">
          <span className="font-semibold text-teal-deep">
            {t('report.nextStep', { step: t(`scale.step${highlight.nextStep.step}`) })}
          </span>{' '}
          {highlight.nextStep.text}
        </p>
      )}
    </li>
  );
};

export const ReportPage = () => {
  const { t, locale } = useI18n();
  const { participantId } = useParams<{ participantId: string }>();
  // Whose programme this is, from the session rather than written in: the
  // product is multi-tenant and the next client is not called BASAC.
  const brand = useAuthStore((state) => state.user?.brand) ?? {
    organization: 'BASAC',
    program: 'Leadership Accelerator',
  };

  const [report, setReport] = useState<ReportDto | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // No id in the URL means the leader's own report: they have no id to ask
    // with, and no screen that would ever tell them one.
    const load = participantId ? fetchReport(participantId) : fetchMyReport();
    load
      .then(setReport)
      .catch((caught: unknown) =>
        setError(caught instanceof Error ? caught.message : t('errors.generic')),
      );
    // Refetched when the language changes, not only when the person does.
    //
    // Most of this page is translated in the browser and switches instantly;
    // the behaviour names, the anchors and the next-step text are chosen by
    // the API and arrive with the payload. Without this they kept whichever
    // language they were fetched in, so switching back to Spanish left the
    // twelve behaviours in English under Spanish headings — visibly stale, and
    // only fixed by reloading the page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [participantId, locale]);

  if (error) return <Centered>{error}</Centered>;
  if (!report) return <Centered>{t('common.loading')}</Centered>;

  const firstName = report.participant.fullName.split(' ')[0] ?? '';
  const foldedSegments = report.confidentialityNotice
    ?.split(',')
    .map((entry) => {
      const [segment, count] = entry.split(':');
      return { segment: segment ?? '', count: Number(count ?? 0) };
    })
    .filter((entry) => entry.count > 0);

  return (
    <article className="report-doc mx-auto max-w-3xl px-6 py-8">
      {/* Neither of these is part of the document: one is a way back into the
          product, the other is a way out of it. Both leave on paper. */}
      <div className="flex items-center justify-between gap-4 print:hidden">
        {/* A leader reading their own report has nowhere to go back to. */}
        {participantId ? (
          <Link
            to={`/participantes/${report.participant.id}`}
            className="font-mono text-[10px] tracking-[0.12em] text-soft uppercase hover:text-teal"
          >
            ← {report.participant.fullName}
          </Link>
        ) : (
          <span />
        )}

        {/*
         * The PDF is this page printed, so there is one design and not two.
         *
         * The label says PDF rather than "print" because a file is what
         * somebody wants; the line under it says where to find it, since the
         * dialog that opens is the browser's and it opens on whatever printer
         * was used last.
         */}
        <button
          type="button"
          onClick={() => window.print()}
          title={t('report.downloadHint')}
          className="cursor-pointer rounded-full border border-line bg-paper px-3.5 py-1.5 font-mono text-[10px] tracking-[0.12em] text-soft uppercase transition hover:border-teal hover:text-teal"
        >
          ↓ {t('report.download')}
        </button>
      </div>

      {/*
       * The masthead, and the only dark surface in the report.
       *
       * Everything below it is paper. The break is the point: this page is
       * handed to somebody about what people said about them, and opening on
       * a title block rather than on a table says it is a document before it
       * is a set of numbers.
       */}
      <header className="relative mt-4 overflow-hidden rounded-2xl bg-masthead break-inside-avoid px-6 py-8 sm:px-9 sm:py-11">
        {/*
         * The rings. Decoration, so they are hidden from a screen reader and
         * sit behind the type. Their centre is past the right edge, which is
         * what makes them read as a surface this block was cut from rather
         * than as a circle placed on it — `slice` keeps them circular at every
         * width, where `meet` would flatten them into ovals.
         *
         * Gone below `sm`. There is no width on a phone where an arc and the
         * title are not fighting for the same pixels, and covering the type is
         * the worse answer of the two.
         */}
        <svg
          aria-hidden
          viewBox="0 0 600 240"
          preserveAspectRatio="xMaxYMid slice"
          className="pointer-events-none absolute inset-0 hidden h-full w-full sm:block"
        >
          {/*
           * One ring, small enough that the curve closes inside the block and
           * you read it as a circle rather than as a crop of a larger one.
           *
           * Three numbers, and they are the ones to move when tuning it. The
           * drawing box is 600 × 240 whatever the block's real size, so these
           * stay put as the page resizes:
           *
           *   r   the circle. At 132 it just fits the height, so the curve is
           *       visible closing top and bottom. Above ~175 it outgrows the
           *       block and the arc starts leaving through the edges instead.
           *   cx  how far the centre sits past the right edge. Raising it
           *       pushes the ring right and shows less of it; lowering it
           *       brings more of the circle into the block, towards the type.
           *   sw  the band. Roughly a third of `r` here.
           */}
          <circle
            cx="590"
            cy="120"
            r="132"
            fill="none"
            stroke="var(--color-masthead-ring)"
            strokeWidth="46"
          />
        </svg>

        {/* Above the rings, and narrow enough never to run under them. */}
        <div className="relative max-w-full sm:max-w-[62%]">
          {/* The client's programme on the client's report. Genoma signs the
              footer, which is where a maker belongs. */}
          <p className="font-mono text-[10px] tracking-[0.16em] text-rail-bright uppercase">
            {brand.organization} 360 · {report.cycle.name}
          </p>
          <h1 className="mt-3 text-[27px] leading-[1.08] font-extrabold tracking-[-0.03em] text-white sm:text-[30px]">
            {t('report.title')}
          </h1>
          <p className="mt-2 font-mono text-[10.5px] tracking-[0.14em] text-masthead-faint uppercase">
            {report.cycle.name}
          </p>

          <div className="mt-7">
            <p className="font-mono text-[10px] tracking-[0.14em] text-masthead-faint uppercase">
              {t('report.preparedFor')}
            </p>
            <p className="mt-1.5 text-[21px] font-extrabold tracking-[-0.02em] text-white sm:text-[24px]">
              {report.participant.fullName}
            </p>
            <p className="mt-0.5 text-[13px] text-masthead-text">
              {t(`roleLevels.${report.participant.roleLevel}`)}
              {report.participant.area && ` · ${report.participant.area}`}
            </p>
          </div>

          <div className="mt-6 flex flex-wrap gap-2">
            <span
              className={[
                'rounded-full border px-3 py-1.5 font-mono text-[9.5px] tracking-[0.12em] uppercase',
                report.status === 'PUBLISHED' || report.status === 'APPROVED'
                  ? 'border-rail-bright/45 text-rail-bright'
                  : 'border-white/15 text-masthead-faint',
              ].join(' ')}
            >
              {report.status === 'PUBLISHED' || report.status === 'APPROVED' ? '✓ ' : ''}
              {report.status ? t(`reportStatus.${report.status}`) : t('report.notApprovedYet')}
            </span>
            <span className="rounded-full border border-white/15 px-3 py-1.5 font-mono text-[9.5px] tracking-[0.12em] text-masthead-text uppercase">
              {t('report.confidential')}
            </span>
          </div>
        </div>
      </header>

      {/* Whoever reviews this report needs its state and the way to change
          it where they arrive, not eleven screens down past the synthesis. */}
      {report.audience === 'REVIEWER' && (
        <ReviewBar
          participantId={report.participant.id}
          status={report.status}
          firstName={firstName}
          onChanged={(status) => setReport({ ...report, status })}
        />
      )}

      {report.blocked && (
        <p className="mt-5 break-inside-avoid rounded-xl border border-[#E0B4A6] bg-[#FDF0EA] px-4 py-3 text-[13.5px] text-[#6B3A24]">
          {t('report.blocked', { min: MIN_SEGMENT_SIZE })}
        </p>
      )}

      {/* 00 — what this is */}
      <SectionHeading number="00" kicker={t('report.kickerAbout')} title={t('report.aboutTitle')} />
      <p className="mt-3 text-[15px] leading-relaxed text-soft">
        {t('report.aboutLead', { name: firstName })}
      </p>
      <div className="mt-4 grid gap-2.5 sm:grid-cols-3">
        {([1, 3, 5] as const).map((step) => (
          <div key={step} className="break-inside-avoid rounded-xl border border-line bg-paper px-3.5 py-3">
            <p className="font-mono text-[10px] font-bold tracking-[0.1em] text-teal-deep uppercase">
              {step} · {t(`scale.step${step}`)}
            </p>
            <p className="mt-1 text-[12.5px] text-soft">{t(`report.rung${step}`)}</p>
          </div>
        ))}
      </div>

      {/* 01 — the numbers */}
      <SectionHeading
        number="01"
        kicker={t('report.kickerQuantitative')}
        title={t('report.behaviorsTitle')}
      />
      <p className="mt-3 mb-4 text-[14px] text-soft">{t('report.behaviorsLead')}</p>
      <BehaviorDotPlot behaviors={report.behaviors} series={report.series} />

      {foldedSegments && foldedSegments.length > 0 && (
        <p className="mt-4 break-inside-avoid rounded-xl border border-line bg-sand px-3.5 py-2.5 text-[12.5px] text-soft">
          {t('report.foldedNotice', {
            segments: foldedSegments
              .map((entry) => `${t(`relationships.${entry.segment}`)} (${entry.count})`)
              .join(', '),
            min: MIN_SEGMENT_SIZE,
          })}
        </p>
      )}

      {/* 02 — what the numbers say */}
      <SectionHeading
        number="02"
        kicker={t('report.kickerInterpretation')}
        title={t('report.interpretationTitle')}
      />

      <h3 className="mt-5 text-[16px] font-bold">{t('report.strengths')}</h3>
      <p className="text-[13px] text-soft">{t('report.strengthsLead')}</p>
      <ul className="mt-2 list-none rounded-2xl border border-line bg-paper px-4 py-2 shadow-card [&>li]:break-inside-avoid">
        {report.strengths.map((highlight) => (
          <HighlightCard key={highlight.behaviorId} highlight={highlight} />
        ))}
      </ul>

      <h3 className="mt-6 text-[16px] font-bold">{t('report.development')}</h3>
      <p className="text-[13px] text-soft">{t('report.developmentLead')}</p>
      <ul className="mt-2 list-none rounded-2xl border border-line bg-paper px-4 py-2 shadow-card [&>li]:break-inside-avoid">
        {report.developmentAreas.map((highlight) => (
          <HighlightCard key={highlight.behaviorId} highlight={highlight} />
        ))}
      </ul>

      {report.blindSpots.length > 0 && (
        <>
          <h3 className="mt-6 text-[16px] font-bold">{t('report.blindSpots')}</h3>
          <p className="text-[13px] text-soft">{t('report.blindSpotsLead')}</p>
          <ul className="mt-2 list-none rounded-2xl border border-line bg-paper px-4 py-2 shadow-card [&>li]:break-inside-avoid">
            {report.blindSpots.map((highlight) => (
              <HighlightCard key={highlight.behaviorId} highlight={highlight} showGap />
            ))}
          </ul>
        </>
      )}

      {/* 03 — what people wrote */}
      <SectionHeading
        number="03"
        kicker={t('report.kickerQualitative')}
        title={t('report.qualitativeTitle')}
      />
      <p className="mt-3 text-[14px] text-soft">{t('report.qualitativeLead')}</p>

      <CommentSummary
        participantId={report.participant.id}
        feedback={report.openFeedback}
        audience={report.audience}
        onChange={(summary) =>
          setReport((current) =>
            current ? { ...current, openFeedback: { ...current.openFeedback, summary } } : current,
          )
        }
      />

      {report.openFeedback.supervisor && (
        <div className="mt-6">
          <h3 className="text-[16px] font-bold">{t('report.supervisorWords')}</h3>
          <p className="text-[13px] text-soft">{t('report.supervisorWordsLead')}</p>
          <blockquote className="mt-2 break-inside-avoid rounded-2xl border border-tint-line bg-tint px-5 py-4 text-[14.5px] leading-relaxed whitespace-pre-line">
            {report.openFeedback.supervisor}
          </blockquote>
        </div>
      )}

      {report.openFeedback.self && (
        <div className="mt-5">
          <h3 className="font-mono text-[10px] tracking-[0.13em] text-faint uppercase">
            {t('report.ownWords')}
          </h3>
          <blockquote className="mt-2 break-inside-avoid rounded-2xl border border-dashed border-line bg-paper/60 px-5 py-4 text-[14.5px] leading-relaxed whitespace-pre-line">
            {report.openFeedback.self}
          </blockquote>
        </div>
      )}

      {/* 04 — what to do about it */}
      <SectionHeading
        number="04"
        kicker={t('report.kickerAction')}
        title={t('report.actionTitle')}
      />
      <ActionPlan
        participantId={report.participant.id}
        narrative={report.narrative}
        // The same audience that reviews the synthesis writes the plan: a
        // leader reading their own report sees prose, not a text box.
        canEdit={report.audience === 'REVIEWER'}
        onSaved={(narrative) => setReport({ ...report, narrative })}
      />

      <footer className="mt-10 border-t border-line pt-4">
        <p className="text-xs text-faint">
          {t('report.confidentialityNotice')} {t('report.footerOwnership')}
        </p>
        <p className="mt-2 font-mono text-[9.5px] tracking-[0.1em] text-faint uppercase">
          Powered by Genoma
        </p>
      </footer>
    </article>
  );
};
